import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme, alpha } from "@mui/material/styles";
import { motion, useScroll, useTransform, useInView } from "framer-motion";

// Icons
import SchoolIcon from "@mui/icons-material/School";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import GroupsIcon from "@mui/icons-material/Groups";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MapIcon from "@mui/icons-material/Map";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// LOGOS
import swanSolutionsLogo from "../../assets/logos/swansolutions_logo.png";
import figLogo from "../../assets/logos/fig_CROP.png";
import swanAiLabsLogo from "../../assets/logos/swan_ai_labs_logo.png";

// ─────────────────────────────────────────────────────────────
// GLASSMORPHISM CARD COMPONENT
// ─────────────────────────────────────────────────────────────
const GlassCard = ({ children, sx = {}, elevation = 0, ...props }) => {
    const theme = useTheme();
    return (
        <Card
            elevation={elevation}
            sx={{
                background: alpha(theme.palette.background.paper, 0.65),
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
                borderRadius: 4,
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}, 
                            inset 0 1px 0 ${alpha(theme.palette.common.white, 0.4)}`,
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}, 
                                inset 0 1px 0 ${alpha(theme.palette.common.white, 0.5)}`,
                },
                ...sx,
            }}
            {...props}
        >
            {children}
        </Card>
    );
};

// ─────────────────────────────────────────────────────────────
// ANIMATED COUNTER COMPONENT
// ─────────────────────────────────────────────────────────────
const AnimatedCounter = ({ target, suffix = "", prefix = "", duration = 2 }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    useEffect(() => {
        if (!isInView) return;
        let startTime;
        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
            const easeOut = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(easeOut * target));
            if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
    }, [isInView, target, duration]);

    return (
        <span ref={ref}>
            {prefix}{count.toLocaleString()}{suffix}
        </span>
    );
};

// ─────────────────────────────────────────────────────────────
// FEATURE CARD COMPONENT
// ─────────────────────────────────────────────────────────────
const FeatureCard = ({ icon: Icon, title, description, delay = 0 }) => {
    const theme = useTheme();
    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
            <GlassCard sx={{ height: "100%", p: 0 }}>
                <CardContent sx={{ p: 4 }}>
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 3,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
                            mb: 3,
                        }}
                    >
                        <Icon sx={{ fontSize: 32, color: "primary.main" }} />
                    </Box>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            mb: 1.5,
                            color: "primary.main",
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: "text.secondary",
                            lineHeight: 1.7,
                        }}
                    >
                        {description}
                    </Typography>
                </CardContent>
            </GlassCard>
        </motion.div>
    );
};

// ─────────────────────────────────────────────────────────────
// STAT CARD COMPONENT
// ─────────────────────────────────────────────────────────────
const StatCard = ({ value, label, icon: Icon, delay = 0 }) => {
    const theme = useTheme();
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay }}
        >
            <GlassCard
                sx={{
                    textAlign: "center",
                    p: 4,
                    background: alpha(theme.palette.common.white, 0.85),
                }}
            >
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        mx: "auto",
                        mb: 2,
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}
                >
                    <Icon sx={{ fontSize: 28, color: "common.white" }} />
                </Box>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 900,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: 1,
                    }}
                >
                    {value}
                </Typography>
                <Typography
                    variant="subtitle1"
                    sx={{
                        color: "text.secondary",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        fontSize: "0.85rem",
                    }}
                >
                    {label}
                </Typography>
            </GlassCard>
        </motion.div>
    );
};

// ─────────────────────────────────────────────────────────────
// CAPABILITY ITEM COMPONENT
// ─────────────────────────────────────────────────────────────
const CapabilityItem = ({ text, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay }}
    >
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2.5 }}>
            <CheckCircleIcon
                sx={{
                    color: "secondary.main",
                    fontSize: 24,
                    mt: 0.3,
                    filter: "drop-shadow(0 2px 4px rgba(0,207,254,0.3))",
                }}
            />
            <Typography
                variant="body1"
                sx={{
                    color: "text.primary",
                    fontSize: "1.08rem",
                    lineHeight: 1.6,
                }}
            >
                {text}
            </Typography>
        </Stack>
    </motion.div>
);

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
export default function AboutUs() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { scrollYProgress } = useScroll();
    const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
    const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 0.95]);

    const partnerLogos = [figLogo, swanAiLabsLogo, swanSolutionsLogo];

    const features = [
        {
            icon: SchoolIcon,
            title: "K12 Expertise",
            description: "Deep understanding of school district operations, compliance requirements, and the unique challenges educators face daily.",
        },
        {
            icon: SpeedIcon,
            title: "Rapid Deployment",
            description: "Agile development practices mean faster implementations, quicker iterations, and solutions that evolve with your needs.",
        },
        {
            icon: SecurityIcon,
            title: "Enterprise Security",
            description: "Bank-level security protocols, FERPA compliance, and robust data protection for sensitive student and district information.",
        },
        {
            icon: IntegrationInstructionsIcon,
            title: "Seamless Integration",
            description: "API-first architecture connects with your existing systems, SIS platforms, and third-party tools without disruption.",
        },
        {
            icon: SupportAgentIcon,
            title: "Dedicated Support",
            description: "Real humans who understand K12. Direct access to developers, not call centers. Problems solved, not escalated.",
        },
        {
            icon: AutoAwesomeIcon,
            title: "AI-Powered Innovation",
            description: "Cutting-edge artificial intelligence and automation tools that streamline workflows and surface actionable insights.",
        },
    ];

    const capabilities = [
        "Procurement & finance platforms for school nutrition",
        "District-wide compliance & regulatory reporting tools",
        "Special diet management & workflow automation",
        "Automated records & document management systems",
        "API-driven enterprise integrations for K12 ecosystems",
        "AI-powered analytics and intelligent process automation",
        "Real-time dashboards and business intelligence",
        "Mobile-first applications for field staff",
    ];

    return (
        <Box
            sx={{
                bgcolor: "background.default",
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* ═══════════════════════════════════════════════════════════
                HERO SECTION - Full viewport with parallax
               ═══════════════════════════════════════════════════════════ */}
            <Box
                component={motion.section}
                style={{ opacity: heroOpacity, scale: heroScale }}
                sx={{
                    position: "relative",
                    width: "100vw",
                    minHeight: { xs: "85vh", md: "90vh" },
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 3,
                    overflow: "hidden",
                }}
            >
                {/* Animated gradient background */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: `
                            radial-gradient(ellipse 80% 50% at 50% -20%, ${alpha(theme.palette.secondary.main, 0.3)} 0%, transparent 50%),
                            radial-gradient(ellipse 60% 40% at 80% 80%, ${alpha(theme.palette.primary.light, 0.2)} 0%, transparent 50%),
                            linear-gradient(160deg, 
                                ${theme.palette.primary.dark} 0%, 
                                ${theme.palette.primary.main} 25%,
                                #1a5a8a 50%,
                                ${alpha(theme.palette.secondary.dark, 0.9)} 75%,
                                ${theme.palette.secondary.main} 100%
                            )
                        `,
                        zIndex: 0,
                    }}
                />

                {/* Floating glass orbs */}
                {[...Array(5)].map((_, i) => (
                    <Box
                        key={i}
                        component={motion.div}
                        animate={{
                            y: [0, -30, 0],
                            x: [0, 15, 0],
                            scale: [1, 1.05, 1],
                        }}
                        transition={{
                            duration: 6 + i * 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: i * 0.8,
                        }}
                        sx={{
                            position: "absolute",
                            width: { xs: 100 + i * 40, md: 150 + i * 60 },
                            height: { xs: 100 + i * 40, md: 150 + i * 60 },
                            borderRadius: "50%",
                            background: `radial-gradient(circle at 30% 30%, 
                                ${alpha(theme.palette.common.white, 0.15)} 0%, 
                                ${alpha(theme.palette.common.white, 0.02)} 70%,
                                transparent 100%
                            )`,
                            border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                            backdropFilter: "blur(4px)",
                            top: `${15 + i * 15}%`,
                            left: `${10 + i * 18}%`,
                            zIndex: 1,
                            pointerEvents: "none",
                        }}
                    />
                ))}

                {/* Hero content */}
                <Box sx={{ position: "relative", zIndex: 2, maxWidth: 900 }}>
                    <motion.div
                        initial={{ opacity: 0, y: -40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
                    >
                        <Box
                            component="img"
                            src={swanSolutionsLogo}
                            alt="Swan Solutions Logo"
                            sx={{
                                height: { xs: 90, md: 150 },
                                mb: 3,
                                filter: "drop-shadow(0 8px 32px rgba(0,0,0,0.25))",
                            }}
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.2 }}
                    >
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: "2.5rem", sm: "3.2rem", md: "4rem" },
                                color: "common.white",
                                mb: 3,
                                letterSpacing: "-0.03em",
                                textShadow: "0 4px 24px rgba(0,0,0,0.2)",
                                lineHeight: 1.1,
                            }}
                        >
                            Transforming K12
                            <br />
                            <Box
                                component="span"
                                sx={{
                                    background: `linear-gradient(90deg, ${theme.palette.secondary.light} 0%, #ffffff 100%)`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                }}
                            >
                                Operations Technology
                            </Box>
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.4 }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                maxWidth: 680,
                                mx: "auto",
                                color: alpha(theme.palette.common.white, 0.92),
                                fontWeight: 500,
                                fontSize: { xs: "1.1rem", md: "1.35rem" },
                                lineHeight: 1.7,
                                mb: 4,
                            }}
                        >
                            Swan Solutions is a modern EdTech software development company founded in 2020,
                            dedicated to building practical, enterprise-level systems for K12 school districts
                            and the companies that serve them.
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.6 }}
                    >
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            justifyContent="center"
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/contact"
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    borderRadius: 99,
                                    fontWeight: 700,
                                    fontSize: "1.1rem",
                                    py: 1.8,
                                    px: 5,
                                    background: "rgba(255,255,255,0.95)",
                                    color: "primary.main",
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`,
                                    "&:hover": {
                                        background: "rgba(255,255,255,1)",
                                        transform: "translateY(-2px)",
                                        boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.25)}`,
                                    },
                                    transition: "all 0.3s ease",
                                }}
                            >
                                Get Started
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                href="/solutions"
                                sx={{
                                    borderRadius: 99,
                                    fontWeight: 700,
                                    fontSize: "1.1rem",
                                    py: 1.8,
                                    px: 5,
                                    borderColor: alpha(theme.palette.common.white, 0.5),
                                    color: "common.white",
                                    backdropFilter: "blur(10px)",
                                    background: alpha(theme.palette.common.white, 0.08),
                                    "&:hover": {
                                        borderColor: "common.white",
                                        background: alpha(theme.palette.common.white, 0.15),
                                    },
                                }}
                            >
                                Our Solutions
                            </Button>
                        </Stack>
                    </motion.div>
                </Box>

                {/* Scroll indicator */}
                <Box
                    component={motion.div}
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    sx={{
                        position: "absolute",
                        bottom: 40,
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 2,
                    }}
                >
                    <Box
                        sx={{
                            width: 30,
                            height: 50,
                            borderRadius: 15,
                            border: `2px solid ${alpha(theme.palette.common.white, 0.4)}`,
                            display: "flex",
                            justifyContent: "center",
                            pt: 1,
                        }}
                    >
                        <Box
                            component={motion.div}
                            animate={{ y: [0, 16, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                            sx={{
                                width: 6,
                                height: 10,
                                borderRadius: 3,
                                bgcolor: alpha(theme.palette.common.white, 0.7),
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* ═══════════════════════════════════════════════════════════
                STATS SECTION - Impact numbers
               ═══════════════════════════════════════════════════════════ */}
            <Container maxWidth="lg" sx={{ py: { xs: 10, md: 14 }, position: "relative" }}>
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "100%",
                        maxWidth: 600,
                        height: 400,
                        background: `radial-gradient(ellipse at center, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 70%)`,
                        pointerEvents: "none",
                    }}
                />

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <Typography
                        variant="overline"
                        sx={{
                            display: "block",
                            textAlign: "center",
                            color: "secondary.main",
                            fontWeight: 700,
                            letterSpacing: "0.15em",
                            mb: 2,
                        }}
                    >
                        Our Impact
                    </Typography>
                    <Typography
                        variant="h2"
                        sx={{
                            textAlign: "center",
                            fontWeight: 900,
                            color: "primary.main",
                            mb: 2,
                        }}
                    >
                        Trusted Nationwide
                    </Typography>
                    <Typography
                        sx={{
                            textAlign: "center",
                            maxWidth: 650,
                            mx: "auto",
                            color: "text.secondary",
                            fontSize: "1.15rem",
                            mb: 8,
                        }}
                    >
                        From small rural schools to some of the largest urban districts in the country,
                        Swan Solutions powers K12 operations across America.
                    </Typography>
                </motion.div>

                <Grid container spacing={4}>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            value={<AnimatedCounter target={180} suffix="+" />}
                            label="School Districts"
                            icon={SchoolIcon}
                            delay={0}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            value={<AnimatedCounter target={27} />}
                            label="States Served"
                            icon={MapIcon}
                            delay={0.1}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            value={<AnimatedCounter target={5000} suffix="+" />}
                            label="Daily Users"
                            icon={GroupsIcon}
                            delay={0.2}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            value="99.9%"
                            label="Uptime SLA"
                            icon={TrendingUpIcon}
                            delay={0.3}
                        />
                    </Grid>
                </Grid>
            </Container>

            {/* ═══════════════════════════════════════════════════════════
                MISSION SECTION - Glassmorphic cards
               ═══════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    py: { xs: 10, md: 14 },
                    position: "relative",
                    background: `linear-gradient(180deg, 
                        ${theme.palette.background.default} 0%, 
                        ${alpha(theme.palette.primary.main, 0.03)} 50%,
                        ${theme.palette.background.default} 100%
                    )`,
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                            >
                                <Chip
                                    label="Our Mission"
                                    sx={{
                                        mb: 3,
                                        fontWeight: 700,
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                        color: "common.white",
                                        fontSize: "0.85rem",
                                        height: 36,
                                        px: 1,
                                    }}
                                />
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 800,
                                        color: "primary.main",
                                        mb: 3,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Practical Solutions,
                                    <br />
                                    Built With You
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: "1.15rem",
                                        color: "text.secondary",
                                        lineHeight: 1.8,
                                        mb: 3,
                                    }}
                                >
                                    We partner with K12 school districts nationwide to design, build,
                                    and support robust technology solutions that power daily operations,
                                    ensure compliance, and streamline work for administrators and staff.
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: "1.1rem",
                                        color: "text.secondary",
                                        lineHeight: 1.8,
                                    }}
                                >
                                    While our core expertise lies in K12 nutrition, our work extends to
                                    every part of the education operations ecosystem—finance, procurement,
                                    workflow, records management, special diets, and more.
                                </Typography>
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <GlassCard
                                    sx={{
                                        p: 5,
                                        background: `linear-gradient(135deg, 
                                            ${alpha(theme.palette.common.white, 0.9)} 0%, 
                                            ${alpha(theme.palette.secondary.light, 0.1)} 100%
                                        )`,
                                    }}
                                >
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 700,
                                            color: "primary.main",
                                            mb: 4,
                                        }}
                                    >
                                        Our Technology Powers:
                                    </Typography>
                                    {capabilities.map((item, i) => (
                                        <CapabilityItem key={i} text={item} delay={i * 0.08} />
                                    ))}
                                </GlassCard>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ═══════════════════════════════════════════════════════════
                FEATURES GRID - Why Choose Us
               ═══════════════════════════════════════════════════════════ */}
            <Container maxWidth="lg" sx={{ py: { xs: 10, md: 14 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <Typography
                        variant="overline"
                        sx={{
                            display: "block",
                            textAlign: "center",
                            color: "secondary.main",
                            fontWeight: 700,
                            letterSpacing: "0.15em",
                            mb: 2,
                        }}
                    >
                        Why Swan Solutions
                    </Typography>
                    <Typography
                        variant="h2"
                        sx={{
                            textAlign: "center",
                            fontWeight: 900,
                            color: "primary.main",
                            mb: 2,
                        }}
                    >
                        What Makes Us Different
                    </Typography>
                    <Typography
                        sx={{
                            textAlign: "center",
                            maxWidth: 650,
                            mx: "auto",
                            color: "text.secondary",
                            fontSize: "1.15rem",
                            mb: 8,
                        }}
                    >
                        We don't just deliver software—we deliver working solutions to real-world
                        K12 challenges with unmatched expertise and support.
                    </Typography>
                </motion.div>

                <Grid container spacing={4}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <FeatureCard {...feature} delay={index * 0.1} />
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* ═══════════════════════════════════════════════════════════
                PARTNERS SECTION - Logos with glass effect
               ═══════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    py: { xs: 10, md: 12 },
                    background: `linear-gradient(180deg, 
                        ${alpha(theme.palette.primary.main, 0.02)} 0%, 
                        ${alpha(theme.palette.secondary.main, 0.05)} 50%,
                        ${alpha(theme.palette.primary.main, 0.02)} 100%
                    )`,
                }}
            >
                <Container maxWidth="lg">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                textAlign: "center",
                                fontWeight: 800,
                                color: "primary.main",
                                mb: 6,
                            }}
                        >
                            Our Family of Products
                        </Typography>
                    </motion.div>

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={4}
                        justifyContent="center"
                        alignItems="center"
                    >
                        {partnerLogos.map((logo, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.15 }}
                                whileHover={{ scale: 1.05 }}
                            >
                                <GlassCard
                                    sx={{
                                        p: 4,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minWidth: 180,
                                        minHeight: 100,
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={logo}
                                        alt={`Partner ${i + 1}`}
                                        sx={{
                                            height: 60,
                                            maxWidth: 150,
                                            objectFit: "contain",
                                        }}
                                    />
                                </GlassCard>
                            </motion.div>
                        ))}
                    </Stack>
                </Container>
            </Box>

            {/* ═══════════════════════════════════════════════════════════
                CTA SECTION - Final call to action
               ═══════════════════════════════════════════════════════════ */}
            <Box
                sx={{
                    position: "relative",
                    py: { xs: 12, md: 16 },
                    overflow: "hidden",
                }}
            >
                {/* Background gradient */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: `linear-gradient(135deg, 
                            ${theme.palette.primary.dark} 0%, 
                            ${theme.palette.primary.main} 50%,
                            ${alpha(theme.palette.secondary.dark, 0.8)} 100%
                        )`,
                    }}
                />

                {/* Decorative elements */}
                <Box
                    sx={{
                        position: "absolute",
                        top: -200,
                        right: -200,
                        width: 500,
                        height: 500,
                        borderRadius: "50%",
                        background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.2)} 0%, transparent 70%)`,
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        bottom: -150,
                        left: -150,
                        width: 400,
                        height: 400,
                        borderRadius: "50%",
                        background: `radial-gradient(circle, ${alpha(theme.palette.common.white, 0.05)} 0%, transparent 70%)`,
                    }}
                />

                <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <Box
                            sx={{
                                textAlign: "center",
                                color: "common.white",
                            }}
                        >
                            <RocketLaunchIcon
                                sx={{
                                    fontSize: 64,
                                    mb: 3,
                                    color: "secondary.light",
                                    filter: `drop-shadow(0 4px 20px ${alpha(theme.palette.secondary.main, 0.4)})`,
                                }}
                            />
                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 900,
                                    mb: 3,
                                    textShadow: "0 4px 24px rgba(0,0,0,0.2)",
                                }}
                            >
                                Ready to Modernize?
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: "1.15rem", md: "1.3rem" },
                                    maxWidth: 600,
                                    mx: "auto",
                                    mb: 5,
                                    opacity: 0.92,
                                    lineHeight: 1.7,
                                }}
                            >
                                Whether you need district-wide operations platforms, custom nutrition
                                solutions, or enterprise integrations, Swan Solutions is ready to help
                                you transform K12 operations.
                            </Typography>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                justifyContent="center"
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    href="/contact"
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        borderRadius: 99,
                                        fontWeight: 700,
                                        fontSize: "1.15rem",
                                        py: 2,
                                        px: 6,
                                        background: "rgba(255,255,255,0.95)",
                                        color: "primary.main",
                                        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.25)}`,
                                        "&:hover": {
                                            background: "rgba(255,255,255,1)",
                                            transform: "translateY(-3px)",
                                            boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.3)}`,
                                        },
                                        transition: "all 0.3s ease",
                                    }}
                                >
                                    Contact Us Today
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    href="/districts"
                                    sx={{
                                        borderRadius: 99,
                                        fontWeight: 700,
                                        fontSize: "1.15rem",
                                        py: 2,
                                        px: 6,
                                        borderColor: alpha(theme.palette.common.white, 0.5),
                                        borderWidth: 2,
                                        color: "common.white",
                                        "&:hover": {
                                            borderColor: "common.white",
                                            borderWidth: 2,
                                            background: alpha(theme.palette.common.white, 0.1),
                                        },
                                    }}
                                >
                                    View Districts
                                </Button>
                            </Stack>
                        </Box>
                    </motion.div>
                </Container>
            </Box>
        </Box>
    );
}